import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, NetworkError } from "../api/client";
import {
  createPayment,
  getFinancialOverview,
  getPaymentInstructions,
  type BillingType,
  type FinancialInvoice,
  type FinancialOverview,
  type PaymentInstructions,
  type PaymentKind,
} from "../api/payments";
import { useAuth } from "../auth/AuthContext";
import { Button } from "../components/Button";
import { StatusBanner } from "../components/StatusBanner";

const BILLING_OPTIONS: Array<{ value: BillingType; label: string }> = [
  { value: "PIX", label: "PIX" },
  { value: "BOLETO", label: "Boleto" },
  { value: "CREDIT_CARD", label: "Cartão" },
];
type HistoryFilter = "all" | "pending" | "paid" | "overdue" | "cancelled";

function nextMonth(): string {
  const date = new Date();
  date.setDate(1);
  date.setHours(12, 0, 0, 0);
  date.setMonth(date.getMonth() + 1);
  return date.toISOString().slice(0, 10);
}

function defaultDueDate(): string {
  return `${nextMonth().slice(0, 7)}-10`;
}

function formatCurrency(value: string | number): string {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value));
}

function statusLabel(status: string): string {
  return ({ pending: "Pendente", paid: "Pago", overdue: "Em atraso", cancelled: "Cancelada" } as Record<string, string>)[status] ?? "Indisponível";
}

function invoiceLabel(invoice: FinancialInvoice): string {
  return invoice.kind === "donation" ? "Doação institucional" : `Mensalidade ${invoice.competencia?.slice(0, 7) ?? "sem competência"}`;
}

function paymentMethodLabel(method: FinancialInvoice["payment_method"]): string {
  return ({ pix: "PIX", boleto: "boleto", credit_card: "cartão" } as Record<string, string>)[method ?? ""] ?? "método escolhido";
}

function InvoiceHistory({
  invoices,
  historyFilter,
  instructionLoading,
  onInstructions,
}: {
  invoices: FinancialInvoice[];
  historyFilter: HistoryFilter;
  instructionLoading: string | null;
  onInstructions: (invoiceId: string) => void;
}) {
  const filteredInvoices = invoices.filter((invoice) => historyFilter === "all" || invoice.status === historyFilter);
  if (invoices.length === 0) return <p className="finance-page__empty">Nenhuma obrigação financeira registrada para este acesso.</p>;
  if (filteredInvoices.length === 0) return <p className="finance-page__empty">Nenhuma obrigação corresponde a este filtro.</p>;
  return (
    <ul className="finance-page__invoices">
      {filteredInvoices.map((invoice) => <li key={invoice.id} className="finance-page__invoice">
        <div><strong>{invoiceLabel(invoice)}</strong><span>Vencimento {new Date(`${invoice.due_date}T12:00:00`).toLocaleDateString("pt-BR")}</span></div>
        <div><strong>{formatCurrency(invoice.amount)}</strong><span className={`finance-status finance-status--${invoice.status}`}>{statusLabel(invoice.status)}</span></div>
        {(invoice.status === "pending" || invoice.status === "overdue") && <button className="btn btn--secondary finance-page__instructions" type="button" onClick={() => onInstructions(invoice.id)} disabled={instructionLoading === invoice.id}>{instructionLoading === invoice.id ? "Buscando…" : "Ver instruções de pagamento"}</button>}
      </li>)}
    </ul>
  );
}

function FinancialOverviewSection({
  overview,
  historyFilter,
  setHistoryFilter,
  invoiceById,
  instructions,
  instructionLoading,
  onInstructions,
  onRefresh,
}: {
  overview: FinancialOverview;
  historyFilter: HistoryFilter;
  setHistoryFilter: (filter: HistoryFilter) => void;
  invoiceById: Map<string, FinancialInvoice>;
  instructions: PaymentInstructions | null;
  instructionLoading: string | null;
  onInstructions: (invoiceId: string) => void;
  onRefresh: () => void;
}) {
  return (
    <section className="finance-page__overview" aria-labelledby="finance-overview-title">
      <div className="finance-page__section-heading">
        <div><p className="dashboard__eyebrow">Situação confirmada</p><h2 id="finance-overview-title">Suas obrigações</h2></div>
        <button className="btn btn--secondary finance-page__refresh" type="button" onClick={onRefresh}>Atualizar</button>
      </div>
      {overview.invoices.length > 0 && <label className="finance-page__filter field"><span className="field__label">Filtrar histórico</span><select className="field__input" value={historyFilter} onChange={(event) => setHistoryFilter(event.target.value as HistoryFilter)}><option value="all">Todas</option><option value="pending">Pendentes</option><option value="paid">Pagas</option><option value="overdue">Em atraso</option><option value="cancelled">Canceladas</option></select></label>}
      <InvoiceHistory invoices={overview.invoices} historyFilter={historyFilter} instructionLoading={instructionLoading} onInstructions={onInstructions} />
      {instructions && <div className="finance-page__payment-result" role="status" aria-live="polite"><h3>Instruções atuais</h3><p>Use o método {paymentMethodLabel(invoiceById.get(instructions.invoice_id)?.payment_method ?? null)} para concluir esta cobrança.</p>{instructions.instructions.pix?.payload && <p><strong>PIX copia e cola:</strong> <code>{instructions.instructions.pix.payload}</code></p>}{instructions.instructions.payment_url && <a href={instructions.instructions.payment_url} target="_blank" rel="noreferrer">Abrir instruções de pagamento</a>}{instructions.instructions.bank_slip_url && <a href={instructions.instructions.bank_slip_url} target="_blank" rel="noreferrer">Abrir boleto</a>}</div>}
    </section>
  );
}

function ReceiptsSection({ overview, invoiceById }: { overview: FinancialOverview; invoiceById: Map<string, FinancialInvoice> }) {
  return (
    <section className="finance-page__receipts" aria-labelledby="finance-receipts-title">
      <div className="finance-page__section-heading"><div><p className="dashboard__eyebrow">Comprovantes oficiais</p><h2 id="finance-receipts-title">Recibos confirmados</h2></div></div>
      {overview.receipts.length === 0 ? <p className="finance-page__empty">Nenhum recibo foi emitido para este acesso.</p> : <div className="finance-page__receipt-list">
        {overview.receipts.map((receipt) => {
          const invoice = invoiceById.get(receipt.invoice_id);
          return <article className="finance-page__receipt" key={receipt.id}>
            <div className="finance-page__receipt-heading"><div><p className="dashboard__eyebrow">Instituto Espírita Dias da Cruz</p><h3>Recibo de contribuição</h3></div><button className="btn btn--secondary finance-page__print" type="button" onClick={() => window.print()}>Imprimir ou salvar PDF</button></div>
            <dl className="data-list data-list--plain data-list--cols-3"><div><dt>Protocolo</dt><dd>{receipt.receipt_reference}</dd></div><div><dt>Competência</dt><dd>{invoice?.competencia?.slice(0, 7) ?? "Não informada"}</dd></div><div><dt>Valor recebido</dt><dd>{formatCurrency(receipt.amount)}</dd></div><div><dt>Recebido em</dt><dd>{new Date(receipt.received_at).toLocaleDateString("pt-BR")}</dd></div><div><dt>Origem</dt><dd>Pagamento confirmado</dd></div></dl>
          </article>;
        })}
      </div>}
    </section>
  );
}

function ContributionForm({
  kind,
  setKind,
  amount,
  setAmount,
  billingType,
  setBillingType,
  dueDate,
  setDueDate,
  recurring,
  setRecurring,
  submitting,
  result,
  onSubmit,
}: {
  kind: PaymentKind;
  setKind: (value: PaymentKind) => void;
  amount: number;
  setAmount: (value: number) => void;
  billingType: BillingType;
  setBillingType: (value: BillingType) => void;
  dueDate: string;
  setDueDate: (value: string) => void;
  recurring: boolean;
  setRecurring: (value: boolean) => void;
  submitting: boolean;
  result: Awaited<ReturnType<typeof createPayment>> | null;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <section className="finance-page__form-card" aria-labelledby="finance-create-title">
      <p className="dashboard__eyebrow">Pagamento seguro</p>
      <h2 id="finance-create-title">Escolha sua contribuição</h2>
      <p>Mensalidade tem valor institucional fixo. Doação é livre e não altera sua situação mensal.</p>
      <form onSubmit={onSubmit}>
        <fieldset className="finance-page__fieldset"><legend>O que deseja emitir?</legend><div className="finance-page__methods">
          <label className={`finance-page__method ${kind === "monthly_fee" ? "finance-page__method--selected" : ""}`}><input type="radio" name="kind" value="monthly_fee" checked={kind === "monthly_fee"} onChange={() => setKind("monthly_fee")} /><span>Mensalidade de R$ 30,00</span></label>
          <label className={`finance-page__method ${kind === "donation" ? "finance-page__method--selected" : ""}`}><input type="radio" name="kind" value="donation" checked={kind === "donation"} onChange={() => setKind("donation")} /><span>Doação institucional</span></label>
        </div></fieldset>
        {kind === "monthly_fee" ? <>
          <fieldset className="finance-page__fieldset"><legend>Vencimento da próxima competência</legend><label className="field" htmlFor="monthly-due-date"><span className="field__label">Data escolhida</span><input id="monthly-due-date" className="field__input" type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} /><small className="field__hint">O padrão é dia 10. O servidor valida datas alternativas permitidas antes da emissão.</small></label></fieldset>
          <label className="finance-page__recurring"><input type="checkbox" checked={recurring} onChange={(event) => setRecurring(event.target.checked)} /> <span><strong>Quero repetir a mensalidade todos os meses</strong><small>A recorrência fica registrada somente após confirmação do provedor.</small></span></label>
        </> : <fieldset className="finance-page__fieldset"><legend>Valor da doação</legend><label className="field"><span className="field__label">Quanto deseja doar?</span><input className="field__input" type="number" min="0.01" step="0.01" value={amount} onChange={(event) => setAmount(Number(event.target.value))} /></label></fieldset>}
        <fieldset className="finance-page__fieldset"><legend>Como deseja pagar?</legend><div className="finance-page__methods">
          {BILLING_OPTIONS.map((option) => <label className={`finance-page__method ${billingType === option.value ? "finance-page__method--selected" : ""}`} key={option.value}><input type="radio" name="billing_type" value={option.value} checked={billingType === option.value} onChange={() => setBillingType(option.value)} /><span>{option.label}</span></label>)}
        </div></fieldset>
        <div className="form-actions"><Button type="submit" loading={submitting}>{submitting ? "Gerando…" : kind === "monthly_fee" ? "Gerar mensalidade" : "Gerar doação"}</Button></div>
      </form>
      {result && <div className="finance-page__payment-result" role="status"><h3>{result.status === "active" ? "Recorrência registrada" : "Cobrança pronta"}</h3><p>As instruções ficam disponíveis na obrigação após a confirmação do provedor.</p></div>}
    </section>
  );
}

export function ContributionPage() {
  const { state, expireSession } = useAuth();
  const [overview, setOverview] = useState<FinancialOverview | null>(null);
  const [kind, setKind] = useState<PaymentKind>("monthly_fee");
  const [amount, setAmount] = useState(30);
  const [billingType, setBillingType] = useState<BillingType>("PIX");
  const [dueDate, setDueDate] = useState(defaultDueDate);
  const [recurring, setRecurring] = useState(false);
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>("all");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [instructionLoading, setInstructionLoading] = useState<string | null>(null);
  const [instructions, setInstructions] = useState<PaymentInstructions | null>(null);
  const [feedback, setFeedback] = useState<{ tone: "error" | "warning" | "success"; message: string } | null>(null);
  const [result, setResult] = useState<Awaited<ReturnType<typeof createPayment>> | null>(null);

  const accessToken = state.status === "authenticated" ? state.accessToken : null;
  const memberId = state.status === "authenticated" ? state.memberId : null;
  const invoiceById = useMemo(() => new Map((overview?.invoices ?? []).map((invoice) => [invoice.id, invoice])), [overview]);

  async function loadOverview() {
    if (!accessToken) return;
    setLoading(true);
    setFeedback(null);
    try {
      setOverview(await getFinancialOverview(accessToken));
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        expireSession();
        return;
      }
      setFeedback({ tone: error instanceof NetworkError ? "warning" : "error", message: "Não foi possível consultar sua situação financeira agora." });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void loadOverview(); }, [accessToken]);

  async function handleInstructions(invoiceId: string) {
    if (!accessToken) return;
    setInstructionLoading(invoiceId);
    setInstructions(null);
    setFeedback(null);
    try {
      setInstructions(await getPaymentInstructions(accessToken, invoiceId));
    } catch (error) {
      setFeedback({ tone: error instanceof NetworkError ? "warning" : "error", message: error instanceof ApiError ? error.message : "Não foi possível obter as instruções agora." });
    } finally {
      setInstructionLoading(null);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!accessToken || !memberId) return;
    setSubmitting(true);
    setFeedback(null);
    setResult(null);
    setInstructions(null);
    try {
      const response = kind === "monthly_fee"
        ? await createPayment(accessToken, {
            kind,
            competencia: nextMonth(),
            billing_type: billingType,
            due_date: dueDate,
            recurring,
          })
        : await createPayment(accessToken, { kind, amount, billing_type: billingType });
      setResult(response);
      setFeedback({ tone: "success", message: kind === "monthly_fee" ? "Sua mensalidade foi registrada para emissão segura." : "Sua doação foi registrada para emissão segura." });
      await loadOverview();
    } catch (error) {
      setFeedback({ tone: error instanceof NetworkError ? "warning" : "error", message: error instanceof ApiError ? error.message : "Não foi possível gerar a cobrança agora." });
    } finally {
      setSubmitting(false);
    }
  }

  if (state.status !== "authenticated") return null;

  return (
    <main className="finance-page" id="main-content">
      <Link className="page-back" to="/dashboard">Voltar para início</Link>
      <header className="finance-page__intro">
        <p className="dashboard__eyebrow">Vida associativa</p>
        <h1>Mensalidades e contribuições</h1>
        <p>Consulte obrigações confirmadas e gere uma cobrança pelo ambiente seguro do Instituto.</p>
      </header>

      {feedback && <StatusBanner tone={feedback.tone}>{feedback.message}</StatusBanner>}
      {loading && <p className="members-state" role="status" aria-live="polite">Consultando situação financeira…</p>}
      {!loading && overview && <FinancialOverviewSection overview={overview} historyFilter={historyFilter} setHistoryFilter={setHistoryFilter} invoiceById={invoiceById} instructions={instructions} instructionLoading={instructionLoading} onInstructions={(invoiceId) => void handleInstructions(invoiceId)} onRefresh={() => void loadOverview()} />}
      {!loading && overview && <ReceiptsSection overview={overview} invoiceById={invoiceById} />}
      <ContributionForm kind={kind} setKind={setKind} amount={amount} setAmount={setAmount} billingType={billingType} setBillingType={setBillingType} dueDate={dueDate} setDueDate={setDueDate} recurring={recurring} setRecurring={setRecurring} submitting={submitting} result={result} onSubmit={handleSubmit} />
    </main>
  );
}
